import { cn, Spinner } from '@herit-fe/heritage-ui';
import { PULL_TO_REFRESH_THRESHOLD, type PullToRefreshState } from './use-pull-to-refresh';

/** 임계까지 당겼을 때 인디케이터가 감기는 각도 — 정확히 한 바퀴라 이어지는 자동 스핀으로 매끄럽게 인계된다. */
const WIND_UP_DEGREES = 360;

/**
 * Pull-to-refresh 인디케이터 — 스크롤 영역 **상단 고정** 위치의 스피너. 콘텐츠 레이어보다 **위**(z-20)에 떠서
 * 가려지지 않는다(`pointer-events-none` 이라 조작은 그대로 통과). 위치는 당김 거리와 무관하게 고정(임계 높이
 * 밴드의 정중앙) — 스피너는 이동하지 않고 **투명도만** 진행률을 따라 서서히 나타났다 사라진다.
 *
 * 회전은 2단계다. **당기는 동안**엔 공용 `Spinner` 내장 `animate-spin` 을 `animate-none` 으로 눌러(`cn`=twMerge 가
 * 뒤 값 채택) 자동 회전을 끄고, 당긴 만큼 `rotate` 시킨다 — 아직 로딩이 아닌데 혼자 도는 오해를 막고 "감는"
 * 피드백을 준다. **로딩(`refreshing`)** 부터는 인라인 transform 을 걷어 내장 스핀에 회전을 넘긴다(임계에서
 * 한 바퀴를 채운 직후라 이어짐이 자연스럽다).
 *
 * 회전량은 `progress`(임계에서 1로 clamp)가 아니라 **`pull` 원값**에 비례시킨다 — clamp 를 쓰면 임계를 넘는
 * 순간 회전이 **멈춰버린다**. `pull` 은 훅에서 이미 고무줄 감쇠(`damp`)를 거친 값이라, 그대로 비례시키면
 * 당길수록 회전이 **점점 느려지며** 상한에 점근한다(별도 이징 불필요 — 밴드 물리가 회전에 그대로 전이).
 *
 * 색은 임계 통과(`armed`) 시 `muted-foreground` → `primary` 로 바뀐다(햅틱과 동시) — "놓으면 새로고침" 신호.
 *
 * 새로고침 중엔 `offset` 이 임계에 고정돼 계속 드러난 채 돌고, 끝나면 페이드아웃된다. 드래그 중에는 opacity
 * transition 을 꺼 손가락을 즉시 따르고, 놓는 순간 켜져 부드럽게 사라진다(색 전환은 드래그 중에도 유지).
 */
export function PullToRefreshIndicator({
  pull,
  progress,
  dragging,
  armed,
  refreshing,
}: PullToRefreshState) {
  // 임계 = 1바퀴 기준. 임계를 넘어도 계속 증가하되, `pull` 이 감쇠된 값이라 증가폭이 점점 줄어든다.
  const windUp = (pull / PULL_TO_REFRESH_THRESHOLD) * WIND_UP_DEGREES;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-center"
      // 상단 safe-area 만큼 밴드를 내려 시작 — 이 인디케이터는 `AppHeader`(자체 safe-area 패딩 소유) 바깥
      // 레이어라, 보정이 없으면 노치/상태바 위에 스피너가 겹친다. 높이에도 inset 을 더해 content-box 는
      // 임계 높이를 유지(= 스피너가 safe-area 아래 밴드의 정중앙).
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        height: `calc(env(safe-area-inset-top) + ${PULL_TO_REFRESH_THRESHOLD}px)`,
      }}
    >
      <Spinner
        className={cn(
          'size-8',
          !refreshing && 'animate-none',
          // 임계 통과 = "놓으면 새로고침" → primary 로 전환(햅틱과 같은 순간). 로딩 중에도 primary 유지.
          armed || refreshing ? 'text-primary' : 'text-muted-foreground',
        )}
        style={{
          opacity: progress,
          // 로딩 중엔 미지정 — 내장 `animate-spin` 이 transform 을 소유해야 계속 돈다(인라인이 있으면 덮어써 멈춤).
          transform: refreshing ? undefined : `rotate(${windUp}deg)`,
          // 속성별로 전환 조건이 다르다:
          //  · color   — 항상(드래그 중에도) 전환. 임계 통과가 부드럽게 읽혀야 하므로.
          //  · opacity/transform — 드래그 중엔 전환 **없음**(손가락 1:1 추종), 놓은 뒤에만 전환.
          //    특히 transform 을 빼먹으면 취소 시 회전이 0 으로 **즉시 튄다**. 본문 복귀(translateY)와
          //    같은 duration·easing 을 써서 스피너 되감기와 본문이 함께 움직이게 맞춘다.
          //  · 로딩 중 transform 전환 제외 — 내장 `animate-spin` 이 transform 을 소유(전환이 끼면 스핀이 끊긴다).
          transition: dragging
            ? 'color 150ms ease-out'
            : refreshing
              ? 'opacity 200ms ease-out, color 150ms ease-out'
              : 'opacity 200ms ease-out, color 150ms ease-out, transform 300ms cubic-bezier(0.32,0.72,0,1)',
        }}
      />
    </div>
  );
}
